/**
 * Sensar Player verbose logger.
 *
 * Buffers structured log records locally and POSTs them to the API in
 * batches. Replaces direct `console.*` for everything we want to be able
 * to triage after the fact — both routine sync events and crashes.
 *
 * Design rules:
 *   - Synchronous .info/.warn/.error/.debug methods — never await on the
 *     hot path. Records go into an in-memory ring, flushed by a periodic
 *     interval and on shutdown.
 *   - Also mirror to the dev console so local dev still gets coloured
 *     log lines without losing them.
 *   - Cap the ring at MAX_BUFFER. Newest wins on overflow (we already
 *     lose the oldest at the network layer, no point burning RAM).
 *   - One flight at a time — overlapping uploads would duplicate.
 *   - Versions (bundleVersion, shellVersion) are filled in by `setMeta`
 *     once at boot; every record copies the current snapshot.
 *
 * Boot order: instantiate early (in main.tsx / bundle entry) BEFORE
 * sync-manager, then call `start(boardId)` once we know which board we
 * are. Until then logs accumulate locally — they're not lost.
 */

type Level = 'debug' | 'info' | 'warn' | 'error';

interface LogRecord {
  level: Level;
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
}

interface LoggerMeta {
  bundleVersion?: string;
  shellVersion?: string;
}

const MAX_BUFFER = 2000;
const FLUSH_INTERVAL_MS = 30_000;
const BATCH_MAX = 100;

class PlayerLogger {
  private buffer: LogRecord[] = [];
  private meta: LoggerMeta = {};
  private flushing = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private uploader: ((batch: LogRecord[], meta: LoggerMeta) => Promise<void>) | null = null;
  private errorSink: ((message: string, context?: Record<string, unknown>) => void) | null = null;
  private startedBoardId: string | null = null;
  // Single beforeunload listener across the logger's lifetime — adding
  // one per `start()` call would leak handlers when the boardId
  // changes (e.g. after a re-pair without page reload).
  private beforeUnloadHandler: (() => void) | null = null;

  /**
   * Attach the function that ships a batch to the server. Decoupled
   * from this file so we can avoid a circular import with api-client.
   */
  setUploader(fn: (batch: LogRecord[], meta: LoggerMeta) => Promise<void>): void {
    this.uploader = fn;
  }

  /**
   * Attach an out-of-band sink for `error()` calls (e.g. Sentry). Fired
   * synchronously alongside the local buffer + console mirror, so a
   * crash is reported even if the periodic batch flush never runs.
   */
  setErrorSink(fn: (message: string, context?: Record<string, unknown>) => void): void {
    this.errorSink = fn;
  }

  /** Set static identifiers carried with every record. */
  setMeta(meta: Partial<LoggerMeta>): void {
    this.meta = { ...this.meta, ...meta };
  }

  /** Begin periodic flush once we have a boardId. Idempotent. */
  start(boardId: string): void {
    if (this.startedBoardId === boardId) return;
    this.startedBoardId = boardId;

    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => {
      void this.flush();
    }, FLUSH_INTERVAL_MS);

    // Try to flush before the tab closes. Fire-and-forget; navigator.sendBeacon
    // would be better but it can't carry our Auth signature headers.
    // We attach this exactly once across the logger's lifetime.
    if (typeof window !== 'undefined' && !this.beforeUnloadHandler) {
      this.beforeUnloadHandler = () => {
        void this.flush();
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.beforeUnloadHandler && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    this.startedBoardId = null;
  }

  // ── Public log methods ───────────────────────────────────────────

  debug(message: string, context?: Record<string, unknown>): void {
    this.emit('debug', message, context);
    console.debug(message, context ?? '');
  }
  info(message: string, context?: Record<string, unknown>): void {
    this.emit('info', message, context);
    console.log(message, context ?? '');
  }
  warn(message: string, context?: Record<string, unknown>): void {
    this.emit('warn', message, context);
    console.warn(message, context ?? '');
  }
  error(message: string, context?: Record<string, unknown>): void {
    this.emit('error', message, context);
    console.error(message, context ?? '');
    // Fan out to Sentry (or any other registered sink). Wrapped in
    // try/catch so a misbehaving sink can never break the caller.
    if (this.errorSink) {
      try {
        this.errorSink(message, context);
      } catch (err) {
        console.warn('[logger] errorSink threw', err);
      }
    }
  }

  /** Backward-compat shim for `console.log` migrations. Treated as `info`. */
  log(message: string, context?: Record<string, unknown>): void {
    this.info(message, context);
  }

  // ── Inspection helpers (for diagnostic screen) ───────────────────

  getBufferStats() {
    return {
      pending: this.buffer.length,
      maxBuffer: MAX_BUFFER,
      meta: this.meta,
      flushing: this.flushing,
    };
  }

  /**
   * Snapshot of the last `n` log records (newest last). Used by the
   * diagnostic screen to show on-device tail without a Web Inspector.
   * Returns a COPY — caller can mutate freely.
   */
  getRecent(n = 50): LogRecord[] {
    if (this.buffer.length <= n) return this.buffer.slice();
    return this.buffer.slice(this.buffer.length - n);
  }

  /** Force a flush right now. Returns when the in-flight batch resolves. */
  async forceFlush(): Promise<void> {
    await this.flush();
  }

  // ── Internal ─────────────────────────────────────────────────────

  private emit(level: Level, message: string, context?: Record<string, unknown>): void {
    this.buffer.push({
      level,
      message,
      context,
      timestamp: new Date().toISOString(),
    });
    if (this.buffer.length > MAX_BUFFER) {
      // Drop oldest to defend against runaway loggers.
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
    }
  }

  /** Drain the buffer through the uploader. Errors restore the batch. */
  private async flush(): Promise<void> {
    if (this.flushing) return;
    if (!this.uploader) return;
    if (this.buffer.length === 0) return;

    this.flushing = true;
    try {
      while (this.buffer.length > 0) {
        const batch = this.buffer.splice(0, BATCH_MAX);
        try {
          await this.uploader(batch, this.meta);
        } catch (err) {
          // Put the batch back at the front so order is preserved.
          this.buffer.unshift(...batch);
          // Mirror to console so a flush failure isn't silent.
          console.warn('[logger] flush failed; will retry next interval', err);
          break;
        }
      }
    } finally {
      this.flushing = false;
    }
  }
}

export const logger = new PlayerLogger();
export type { LogRecord, LoggerMeta, Level };
